import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { t, useLanguage, locale } from "@/lib/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

function NotificationInbox() {
  useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false), [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, unread_count: 0 });
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const mounted = useRef(true), generation = useRef(0);
  const load = useCallback(async (signal) => {
    const current = ++generation.current;
    try {
      const result = await api.get(`/notifications?offset=${offset}&limit=20`, { signal });
      if (mounted.current && !signal?.aborted && current === generation.current) {
        setData(result.data); setError("");
      }
    } catch (err) {
      if (mounted.current && !signal?.aborted && current === generation.current)
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not load notifications. Please retry.");
    } finally {
      if (mounted.current && !signal?.aborted && current === generation.current) setLoading(false);
    }
  }, [offset]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    let polling = false;
    const refresh = async () => {
      if (document.visibilityState === "hidden" || polling) return;
      polling = true; await load(controller.signal); polling = false;
    };
    setLoading(true); refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    window.addEventListener("notification-activity", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      controller.abort(); clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("notification-activity", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load, open]);
  const act = async (work) => {
    setBusy(true); setError("");
    try { await work(); }
    catch (err) { if (mounted.current) setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update notifications. Please retry."); }
    finally { if (mounted.current) setBusy(false); }
  };
  const view = (item) => act(async () => {
    if (!item.read) await api.post(`/notifications/${encodeURIComponent(item.id)}/read`);
    if (!mounted.current) return;
    setOpen(false); navigate(item.href); await load();
  });
  return <Popover open={open} onOpenChange={(value) => { setOpen(value); if (value) setOffset(0); }}>
    <PopoverTrigger asChild>
      <button type="button" className="relative inline-flex items-center justify-center text-amber-950"
        aria-label={`${t("Notifications")} (${data.unread_count} ${t("unread")})`} title={t("Notifications")} data-testid="notification-bell">
        <Bell size={20} aria-hidden="true" />
        {data.unread_count > 0 && <span className="absolute -top-1 -right-1 min-w-4 rounded-full bg-red-700 px-1 text-[10px] leading-4 text-white" aria-hidden="true">{data.unread_count > 99 ? "99+" : data.unread_count}</span>}
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" className="z-[80] w-[min(24rem,calc(100vw-1rem))] p-0" aria-label={t("Notifications")}>
      <div className="flex items-center justify-between gap-2 border-b p-3">
        <h2 className="font-semibold">{t("Notifications")}</h2>
        <Button size="sm" variant="ghost" disabled={busy || !data.unread_count} onClick={() => act(async () => { await api.post("/notifications/read-all"); await load(); })}>{t("Mark all as read")}</Button>
      </div>
      {error && <div className="p-3"><p role="alert" className="text-sm text-red-800">{t(error)}</p><Button size="sm" variant="outline" disabled={busy} onClick={() => load()}>{t("Retry")}</Button></div>}
      {loading && <p role="status" className="p-3 text-sm">{t("Loading notifications...")}</p>}
      {!loading && !error && !data.items.length && <p className="p-4 text-sm text-stone-600">{t("No notifications yet.")}</p>}
      <ul className="max-h-[60dvh] overflow-y-auto">
        {data.items.map((item) => <li key={item.id} className="border-b last:border-b-0">
          <button type="button" disabled={busy} onClick={() => view(item)} className={`w-full p-3 text-left hover:bg-amber-100 focus-visible:outline focus-visible:outline-amber-700 ${item.read ? "bg-white" : "bg-amber-50"}`}>
            <span className={`block text-sm ${item.read ? "" : "font-semibold"}`}>{!item.read && <span className="mr-2 inline-block h-2 w-2 rounded-full bg-amber-700" aria-label={t("Unread")} />}{t(item.title)}</span>
            <time dateTime={item.created_at} className="mt-1 block text-xs text-stone-500">{new Date(item.created_at).toLocaleString(locale())}</time>
          </button>
        </li>)}
      </ul>
      {data.total > 20 && <div className="flex items-center justify-between border-t p-2">
        <Button size="sm" variant="ghost" disabled={busy || loading || !offset} onClick={() => setOffset(offset - 20)}>{t("Previous")}</Button>
        <Button size="sm" variant="ghost" disabled={busy || loading || offset + 20 >= data.total} onClick={() => setOffset(offset + 20)}>{t("Next")}</Button>
      </div>}
    </PopoverContent>
  </Popover>;
}

export default function NotificationBell() {
  const { user, ready } = useAuth();
  if (!ready || !user || user.role === "guest") return null;
  return <NotificationInbox key={`${user.id}:${user.role}`} />;
}
